import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Table, Button, Alert } from 'react-bootstrap';
import { newsApi, categoriesApi } from '../services/api';
import { News, Category } from '../types';
import '../styles/pages.css';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState<News[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [newsResponse, categoriesData] = await Promise.all([
        newsApi.getAll(),
        categoriesApi.getActive(),
      ]);
      setNews(newsResponse.items);
      setCategories(categoriesData);
    } catch (error: any) {
      console.error('Error loading data:', error);

      // Se for erro de autenticação, redireciona para login
      if (error.message?.includes('401') || error.message?.includes('expirada')) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir esta notícia?')) {
      try {
        await newsApi.delete(id);
        setSuccess('Notícia excluída com sucesso!');
        fetchData();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err: any) {
        console.error('Error deleting news:', err);
        setError(err.message || 'Erro ao excluir notícia');
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getCategoryName = (categoryId: number) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.nome || 'Sem categoria';
  };

  return (
    <div>
      <div className="mb-4">
        <h2><i className="bi bi-newspaper me-2"></i>Gerenciar Notícias</h2>
      </div>

      <div>
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Notícias ({news.length})</h2>
          <Button variant="primary" onClick={() => navigate('/admin/noticias/criar')}>
            <i className="bi bi-plus-lg me-2"></i>
            Nova Notícia
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden' }}>
            <Table responsive hover>
              <thead style={{ background: 'var(--color-primary)', color: 'white' }}>
                <tr>
                  <th style={{ width: '80px' }}>Imagem</th>
                  <th>Título</th>
                  <th style={{ width: '150px' }}>Categoria</th>
                  <th style={{ width: '120px' }}>Data</th>
                  <th style={{ width: '220px' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {news.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-5 text-muted">
                      Nenhuma notícia cadastrada. Clique em "Nova Notícia" para começar.
                    </td>
                  </tr>
                ) : (
                  news.map((item) => (
                    <tr key={item.id}>
                      <td>
                        {item.imagemUrl ? (
                          <img
                            src={item.imagemUrl}
                            alt={item.titulo}
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: '60px',
                              height: '60px',
                              background: 'var(--color-gray-light)',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <i className="bi bi-image text-muted"></i>
                          </div>
                        )}
                      </td>
                      <td>
                        <div>
                          <strong>{item.titulo}</strong>
                          {item.subtitulo && (
                            <>
                              <br />
                              <small className="text-muted">{item.subtitulo}</small>
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="badge bg-secondary">{getCategoryName(item.categoriaId)}</span>
                      </td>
                      <td>{formatDate(item.dataNoticia)}</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => navigate(`/admin/noticias/editar/${item.id}`)}
                          title="Editar"
                        >
                          <i className="bi bi-pencil"></i>
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-2"
                          onClick={() => window.open(`/noticias/${item.id}`, '_blank')}
                          title="Visualizar"
                        >
                          <i className="bi bi-eye"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(item.id)}
                          title="Excluir"
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
